import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { components } from 'react-select';
import type { GroupBase, MenuListProps } from 'react-select';

import useIsVisibleOnScreen from '../../../hooks/useIsVisibleOnScreen';
import Button from '../Button/Button';

import Select from './Select';
import type { SelectProps } from './Select';

import css from './Select.module.less';

/**
 * The parts of a React Query infinite query that the select needs. The result of `useInfiniteQuery` can be passed as is.
 */
type PagedQuery = {
  hasNextPage?: boolean;
  isFetching: boolean;
  isError: boolean;
  fetchNextPage: () => unknown;
};

type NextPage = {
  canFetchNextPage: boolean;
  hasFailed: boolean;
  fetchNextPage: () => unknown;
};

const NextPageContext = createContext<NextPage>({
  canFetchNextPage: false,
  hasFailed: false,
  fetchNextPage: () => null,
});

type MenuEndProps = {
  onReached: () => unknown;
};

/**
 * A marker for the end of the menu, which reports once it is on screen. Watching a marker (instead of scroll events)
 * covers every way of reaching the end of the list: mouse wheel, touch, dragging the scrollbar, arrow keys, and a
 * list too short to scroll at all.
 */
const MenuEnd = ({ onReached }: MenuEndProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isOnScreen = useIsVisibleOnScreen(ref);

  useEffect(() => {
    if (isOnScreen) {
      onReached();
    }
  }, [isOnScreen, onReached]);

  return <div ref={ref} className={css.menuEnd} />;
};

type LoadErrorProps = {
  hasOptions: boolean;
  onRetry: () => unknown;
};

/**
 * Tells the user a page of options failed to load, and lets them ask for it again.
 */
const LoadError = ({ hasOptions, onRetry }: LoadErrorProps) => (
  <div role="alert" className={css.loadError}>
    {hasOptions ? "Couldn't load more." : "Couldn't load options."}
    <Button type="anchor" size="small" onClick={() => { onRetry(); }}>
      Retry
    </Button>
  </div>
);

const InfiniteMenuList = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: MenuListProps<Option, IsMulti, Group>,
) => {
  const { canFetchNextPage, hasFailed, fetchNextPage } = useContext(NextPageContext);
  const hasOptions = props.options.length > 0;

  /*
   * The marker only reports when it first comes on screen. If the next page is too short to push it out of view, a
   * marker kept from the page before would never report again, and the page after would never load. The marker is
   * not always removed while a page loads either: a response that comes back right away can land in the same render
   * as the start of its request. So the key ties the marker to the number of loaded options. Every new page changes
   * that number, which makes React throw the old marker away and mount a new one.
   */
  return (
    <components.MenuList {...props}>
      {/* With no options the children are only the "No options" message, which is wrong after a failure. */}
      {(hasOptions || !hasFailed) && props.children}
      {hasFailed && <LoadError hasOptions={hasOptions} onRetry={fetchNextPage} />}
      {canFetchNextPage && <MenuEnd key={props.options.length} onReached={fetchNextPage} />}
    </components.MenuList>
  );
};

type Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> = SelectProps<Option, IsMulti, Group> & {
  /**
   * The infinite query that loads the options. Its next page is fetched each time the user reaches the end of the menu.
   */
  query: PagedQuery;
};

/**
 * A Select for options that are loaded one page at a time with a React Query infinite query.
 *
 * The caller owns the query: flatten `query.data.pages` into `options`, and feed `onInputChange` into the query's
 * search param to search on the server. Options are never filtered in the browser, since only some of them are loaded.
 */
export const InfiniteSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ query, ...props }: Props<Option, IsMulti, Group>) => {
  const { hasNextPage, isFetching, isError, fetchNextPage } = query;

  //  A failed query still reports a next page, so stop on errors or the same page would be requested forever.
  const canFetchNextPage = Boolean(hasNextPage) && !isFetching && !isError;
  //  A failed query keeps saying it failed while it is being retried, so wait for the retry before saying so again.
  const hasFailed = isError && !isFetching;
  const nextPage = useMemo(
    () => ({ canFetchNextPage, hasFailed, fetchNextPage }),
    [canFetchNextPage, hasFailed, fetchNextPage],
  );

  return (
    <NextPageContext.Provider value={nextPage}>
      <Select
        {...props}
        isLoading={isFetching || props.isLoading}
        filterOption={null}
        components={{ ...props.components, MenuList: InfiniteMenuList }}
      />
    </NextPageContext.Provider>
  );
};
